import { BookmarkPlus, ChevronDown, RotateCcw } from "lucide-react";
import type { SimulationInput } from "../../shared/contracts";
import { currency, termLabel } from "../lib/format";

interface SavedScenario {
  id: string;
  name: string;
  input: SimulationInput;
}

interface InputPanelProps {
  input: SimulationInput;
  onChange: (input: SimulationInput) => void;
  onSave: () => void;
  savedScenarios: SavedScenario[];
  onLoad: (id: string) => void;
  onReset: () => void;
}

function NumericField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  hint,
  prefix
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  hint: string;
  prefix?: string;
}) {
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">{label}</span>
      <span className="field__control">
        {prefix && <span className="field__prefix">{prefix}</span>}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </span>
      <span className="field__hint">{hint}</span>
    </label>
  );
}

export function InputPanel({
  input,
  onChange,
  onSave,
  savedScenarios,
  onLoad,
  onReset
}: InputPanelProps) {
  const financedAmount = Math.max(0, input.propertyValue - input.downPayment);
  const downPaymentPct =
    input.propertyValue > 0 ? (input.downPayment / input.propertyValue) * 100 : 0;

  return (
    <section className="input-panel" aria-labelledby="simulation-data-title">
      <div className="section-kicker">01 · Sua base</div>
      <div className="input-panel__heading">
        <div>
          <h2 id="simulation-data-title">Dados da compra</h2>
          <p>Ajuste e veja todas as instituições reagirem juntas.</p>
        </div>
        <button className="icon-button" type="button" onClick={onReset} aria-label="Restaurar exemplo">
          <RotateCcw size={17} aria-hidden="true" />
        </button>
      </div>

      <div className="field-grid">
        <NumericField
          id="property-value"
          label="Valor do imóvel"
          value={input.propertyValue}
          onChange={(propertyValue) =>
            onChange({
              ...input,
              propertyValue,
              downPayment: Math.min(input.downPayment, Math.max(0, propertyValue - 1))
            })
          }
          min={50_000}
          max={100_000_000}
          step={5_000}
          prefix="R$"
          hint={currency(input.propertyValue)}
        />
        <NumericField
          id="down-payment"
          label="Entrada"
          value={input.downPayment}
          onChange={(downPayment) =>
            onChange({
              ...input,
              downPayment: Math.min(Math.max(0, downPayment), input.propertyValue - 1)
            })
          }
          min={0}
          max={Math.max(0, input.propertyValue - 1)}
          step={5_000}
          prefix="R$"
          hint={`${currency(input.downPayment)} · ${downPaymentPct.toFixed(1).replace(".", ",")}%`}
        />
        <NumericField
          id="age"
          label="Idade do proponente"
          value={input.age}
          onChange={(age) => onChange({ ...input, age })}
          min={18}
          max={100}
          hint={`${input.age} anos hoje`}
        />
        <NumericField
          id="monthly-income"
          label="Renda familiar mensal"
          value={input.monthlyIncome}
          onChange={(monthlyIncome) => onChange({ ...input, monthlyIncome })}
          min={1_000}
          max={100_000_000}
          step={500}
          prefix="R$"
          hint={currency(input.monthlyIncome)}
        />
      </div>

      <label className="range-field" htmlFor="term">
        <span>
          <strong>Prazo desejado</strong>
          <output htmlFor="term">{termLabel(input.termMonths)}</output>
        </span>
        <input
          id="term"
          type="range"
          min={60}
          max={420}
          step={12}
          value={input.termMonths}
          onChange={(event) => onChange({ ...input, termMonths: Number(event.target.value) })}
        />
        <span className="range-field__scale" aria-hidden="true">
          <small>5 anos</small>
          <small>20 anos</small>
          <small>35 anos</small>
        </span>
      </label>

      <div className="financing-readout" aria-live="polite">
        <span>Valor a financiar</span>
        <strong>{currency(financedAmount)}</strong>
        <small>{(100 - downPaymentPct).toFixed(1).replace(".", ",")}% do imóvel</small>
      </div>

      <div className="scenario-actions">
        <button className="button button--soft" type="button" onClick={onSave}>
          <BookmarkPlus size={17} aria-hidden="true" />
          Salvar cenário
        </button>
        {savedScenarios.length > 0 && (
          <label className="scenario-select">
            <span className="sr-only">Abrir cenário salvo</span>
            <select defaultValue="" onChange={(event) => onLoad(event.target.value)}>
              <option value="" disabled>
                Abrir salvo
              </option>
              {savedScenarios.map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </label>
        )}
      </div>
    </section>
  );
}

